import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import style from "./style.module.css";
import TruncatedText from "../../Components/TruncatedText";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import Barcode from 'react-barcode';
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { parseApiError } from "../../utils/parseApiError";
import { API_BASE, resolveImageUrl } from "../../config/api";
import BarcodeScannerModal from "../../Components/BarcodeScannerModal";
import Pagination from "../../Components/Pagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { usePosSettings } from "../../utils/posSettings";

const MIN_SEARCH_LENGTH = 2;

// Matches ProductTags.tag_name's VARCHAR(100) — catch an overlong tag here
// with a friendly message instead of letting it hit the backend as a raw 500.
const TAG_MAX_LENGTH = 100;

interface Product {
  product_id: number;
  category_id: number | null;
  brand_id: number | null;
  category_name?: string | null;
  brand_name?: string | null;
  Category?: { category_id: number; category_name: string } | null;
  Brand?: { brand_id: number; brand_name: string } | null;
  product_name: string;
  product_description: string;
  price: number;
  purchase_price: number | null;
  quantity: number;
  unit_value?: number | string | null;
  unit?: string | null;
  weight?: string | null;
  barcode: string;
  tags: string[];
  is_active: boolean;
  product_image: string;
  product_image_url: string;
  created_at: string;
  updated_at: string;
}

interface EditProduct {
  product_id?: number;
  category_id?: number | null;
  brand_id?: number | null;
  product_name: string;
  product_description: string;
  price: string | number;
  purchase_price: string | number;
  quantity: string | number;
  unit_value: string | number;
  unit: string;
  barcode: string;
  tags: string[];
  product_image: string;
  product_image_url?: string;
}

interface Category {
  category_id: number;
  category_name: string;
}

interface Brand {
  brand_id: number;
  brand_name: string;
}

interface Shop {
  shop_id: number;
  shop_name: string;
}

// Rendered twice: once normally (on-screen preview inside the modal) and
// once via a portal into #print-root (see the print button's onClick below)
// — the portal copy is what actually gets printed. Keeping this as one
// component means the preview can never silently drift from print output.
const BarcodeLabelContent = ({ product }: { product: Product }) => (
  <>
    <p className={style.printProductName}>{product.product_name}</p>
    <Barcode
      value={product.barcode}
      format="CODE128"
      width={1.2}
      height={42}
      displayValue={true}
      fontSize={10}
      margin={2}
    />
  </>
);

const ProductPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  // Unscoped — feeds the read-only product table's getCategoryName/getBrandName
  // lookups across every product regardless of shop. Never reassigned to a
  // shop-scoped list; the modal's dropdowns use their own state below.
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [selectedShopId, setSelectedShopId] = useState<string>("");
  // Shop-scoped lists shown in the Add/Edit modal's Category/Brand selects.
  const [modalCategories, setModalCategories] = useState<Category[]>([]);
  const [modalBrands, setModalBrands] = useState<Brand[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editProduct, setEditProduct] = useState<EditProduct>({
    product_name: "",
    product_description: "",
    price: "",
    purchase_price: "",
    quantity: "",
    unit_value: "",
    unit: "",
    barcode: "",
    tags: [],
    product_image: ""
  });
  const [tagInput, setTagInput] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [brandSearch, setBrandSearch] = useState("");
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  // Scanner state
  const [scanInput, setScanInput] = useState("");
  const [isScanLoading, setIsScanLoading] = useState(false);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const [highlightedProductId, setHighlightedProductId] = useState<number | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showScannerModal, setShowScannerModal] = useState<null | "add" | "search">(null);

  // Print modal state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printProduct, setPrintProduct] = useState<Product | null>(null);
  const [printBarcodeInput, setPrintBarcodeInput] = useState("");
  const [isAssigningBarcode, setIsAssigningBarcode] = useState(false);

  // Scopes the print-barcode-label CSS (index.css's @media print rules) to
  // exactly the window where this modal has a printable barcode — both the
  // `body.printing-barcode-label` class those rules key off of, and the
  // @page size/margin rule itself, only exist in the DOM while this is
  // true, and are torn down again on close/unmount. That's what stops an
  // unrelated print elsewhere in the app from going blank (see index.css's
  // comment on why @page can't just be scoped by a CSS selector directly).
  const isPrintingBarcodeLabel = showPrintModal && !!printProduct?.barcode;
  useEffect(() => {
    if (!isPrintingBarcodeLabel) return;

    document.body.classList.add('printing-barcode-label');

    const styleEl = document.createElement('style');
    // Kept as a literal, not var(--label-w)/var(--label-h) — CSS custom
    // property resolution inside @page descriptors is inconsistent across
    // browsers. Must be changed alongside --label-w/--label-h in index.css
    // if the label size changes.
    styleEl.textContent = '@page { size: 2in 2in; margin: 0mm; }';
    document.head.appendChild(styleEl);

    return () => {
      document.body.classList.remove('printing-barcode-label');
      styleEl.remove();
    };
  }, [isPrintingBarcodeLabel]);

  // Debounce the search box so it doesn't refetch on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // A new search term invalidates the current page — go back to page 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
    fetchShops();
    scanInputRef.current?.focus();
  }, []);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearchTerm, cookies.auth]);

  // The Add/Edit modal never used to push any browser-history entry, so a
  // back gesture skipped straight past this page. Reflecting "a modal is
  // open" in the URL means the browser back action pops just this param —
  // landing back on a bare /products with the modal closed — instead of
  // leaving the page entirely.
  useEffect(() => {
    if (!searchParams.get('modal')) {
      setShowAddModal(false);
      setShowEditModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const settings = usePosSettings();
  const pageSize = settings.itemsPerPage || 25;

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      // Search is a plain substring match server-side (LIKE '%x%' on name/
      // description/barcode/tags) — a single character matches almost every
      // product containing it anywhere, which isn't a useful result set.
      // Require a couple characters before it's sent at all.
      const trimmedSearch = debouncedSearchTerm.trim();
      if (trimmedSearch.length >= MIN_SEARCH_LENGTH) params.set('search', trimmedSearch);
      const response = await authenticatedFetch(`${API_BASE}/product?${params}`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const result = await response.json();
        setProducts(result.data || []);
        setTotalPages(result.totalPages || 1);
        setTotalItems(result.total || 0);
      } else {
        snack.onResponse({ message: "Failed to load products", status: response.status });
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      snack.onResponse({ message: "Network error loading products", status: 500 });
    }
  }, [page, pageSize, debouncedSearchTerm, cookies.auth, snack]);

  // Unscoped — populates `categories`, used only by the read-only table's
  // getCategoryName fallback lookup. Fetches all pages if > 100 categories exist.
  const fetchCategories = useCallback(async () => {
    try {
      let allCategories: Category[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await authenticatedFetch(`${API_BASE}/category?page=${currentPage}&limit=100`, {
          method: 'GET'
        }, cookies.auth);

        if (response.ok) {
          const data = await response.json();
          const pageCategories = Array.isArray(data?.data) ? data.data : [];
          if (pageCategories.length === 0) break;
          allCategories = allCategories.concat(pageCategories);
          totalPages = data?.totalPages || data?.pagination?.totalPages || 1;
          currentPage++;
        } else {
          snack.onResponse({ message: "Failed to load categories", status: response.status });
          break;
        }
      } while (currentPage <= totalPages);

      setCategories(allCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      snack.onResponse({ message: "Network error loading categories", status: 500 });
    }
  }, [cookies.auth, snack]);

  const fetchBrands = useCallback(async () => {
    try {
      let allBrands: Brand[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await authenticatedFetch(`${API_BASE}/brand?page=${currentPage}&limit=100`, {
          method: 'GET'
        }, cookies.auth);

        if (response.ok) {
          const data = await response.json();
          const pageBrands = Array.isArray(data?.data) ? data.data : [];
          if (pageBrands.length === 0) break;
          allBrands = allBrands.concat(pageBrands);
          totalPages = data?.totalPages || data?.pagination?.totalPages || 1;
          currentPage++;
        } else {
          snack.onResponse({ message: "Failed to load brands", status: response.status });
          break;
        }
      } while (currentPage <= totalPages);

      setBrands(allBrands);
    } catch (error) {
      console.error("Error fetching brands:", error);
      snack.onResponse({ message: "Network error loading brands", status: 500 });
    }
  }, [cookies.auth, snack]);

  // Shop-scoped — populates `modalCategories`/`modalBrands`, shown only in
  // the Add/Edit modal's dropdowns once a Shop has been selected. A silent
  // failure here would leave the modal showing an empty, unexplained
  // dropdown with no indication anything went wrong — surface it instead.
  const fetchModalCategories = useCallback(async (shopId: string) => {
    try {
      let allCategories: Category[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await authenticatedFetch(`${API_BASE}/category?page=${currentPage}&limit=100&shop_id=${shopId}`, {
          method: 'GET'
        }, cookies.auth);

        if (response.ok) {
          const data = await response.json();
          const pageCategories = Array.isArray(data?.data) ? data.data : [];
          if (pageCategories.length === 0) break;
          allCategories = allCategories.concat(pageCategories);
          totalPages = data?.totalPages || data?.pagination?.totalPages || 1;
          currentPage++;
        } else {
          snack.onResponse({ message: "Failed to load categories for this shop", status: response.status });
          break;
        }
      } while (currentPage <= totalPages);

      setModalCategories(allCategories);
    } catch (error) {
      console.error("Error fetching shop categories:", error);
      snack.onResponse({ message: "Network error loading categories for this shop", status: 500 });
    }
  }, [cookies.auth, snack]);

  const fetchModalBrands = useCallback(async (shopId: string) => {
    try {
      let allBrands: Brand[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await authenticatedFetch(`${API_BASE}/brand?page=${currentPage}&limit=100&shop_id=${shopId}`, {
          method: 'GET'
        }, cookies.auth);

        if (response.ok) {
          const data = await response.json();
          const pageBrands = Array.isArray(data?.data) ? data.data : [];
          if (pageBrands.length === 0) break;
          allBrands = allBrands.concat(pageBrands);
          totalPages = data?.totalPages || data?.pagination?.totalPages || 1;
          currentPage++;
        } else {
          snack.onResponse({ message: "Failed to load brands for this shop", status: response.status });
          break;
        }
      } while (currentPage <= totalPages);

      setModalBrands(allBrands);
    } catch (error) {
      console.error("Error fetching shop brands:", error);
      snack.onResponse({ message: "Network error loading brands for this shop", status: 500 });
    }
  }, [cookies.auth, snack]);

  const fetchShops = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/shop?page=1&limit=100`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setShops(Array.isArray(data?.data) ? data.data : []);
      } else {
        snack.onResponse({ message: "Failed to load shops", status: response.status });
      }
    } catch (error) {
      console.error("Error fetching shops:", error);
      snack.onResponse({ message: "Network error loading shops", status: 500 });
    }
  }, [cookies.auth, snack]);

  // A product has no direct shop_id — it's derived transitively via whichever
  // of category_id/brand_id is set (category takes precedence, matching the
  // form's field order). A product with neither is a data edge case: leave
  // selectedShopId unset and let Category/Brand stay in the "select a shop
  // first" disabled state rather than guessing.
  const resolveShopIdForProduct = async (categoryId?: number | null, brandId?: number | null): Promise<string> => {
    try {
      if (categoryId) {
        const res = await authenticatedFetch(`${API_BASE}/category/${categoryId}`, { method: 'GET' }, cookies.auth);
        if (res.ok) {
          const data = await res.json();
          if (data?.shop_id) return String(data.shop_id);
        }
      }
      if (brandId) {
        const res = await authenticatedFetch(`${API_BASE}/brand/${brandId}`, { method: 'GET' }, cookies.auth);
        if (res.ok) {
          const data = await res.json();
          if (data?.shop_id) return String(data.shop_id);
        }
      }
    } catch (error) {
      console.error("Error resolving shop for product:", error);
      snack.onResponse({ message: "Couldn't determine this product's shop — please select one manually", status: 500 });
    }
    return "";
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Shared by both the comma-triggered and Enter-triggered commit paths in
  // the tag chip input — trims, rejects empty/duplicate, and enforces the
  // same VARCHAR(100) limit as ProductTags.tag_name with a friendly message
  // instead of letting an overlong tag reach the backend as a raw 500.
  const commitTag = (rawValue: string) => {
    const newTag = rawValue.trim();
    if (!newTag) return;
    if (newTag.length > TAG_MAX_LENGTH) {
      alert(`Tag "${newTag.slice(0, 20)}..." is too long — tags must be ${TAG_MAX_LENGTH} characters or fewer.`);
      return;
    }
    if (editProduct.tags.some(t => t.toLowerCase() === newTag.toLowerCase())) return;
    setEditProduct({...editProduct, tags: [...editProduct.tags, newTag]});
  };

  const handleAdd = () => {
    setEditProduct({
      product_name: "",
      product_description: "",
      price: "",
      purchase_price: "",
      quantity: "",
      unit_value: "",
      unit: "",
      barcode: "",
      tags: [],
      product_image: ""
    });
    setTagInput("");
    setImageFile(null);
    resetDropdowns();
    setSelectedShopId("");
    setModalCategories([]);
    setModalBrands([]);
    setShowAddModal(true);
    setSearchParams({ modal: 'add' });
  };

  const handleUpdate = async () => {
    if (selectedProducts.length === 0) {
      alert("Please select at least one product to update");
      return;
    }

    const productToEdit = products.find(product => product.product_id === selectedProducts[0]);
    if (productToEdit) {
      setEditProduct({
        product_id: productToEdit.product_id,
        category_id: productToEdit.category_id,
        brand_id: productToEdit.brand_id,
        product_name: productToEdit.product_name,
        product_description: productToEdit.product_description,
        price: productToEdit.price,
        purchase_price: productToEdit.purchase_price ?? "",
        quantity: productToEdit.quantity,
        unit_value: productToEdit.unit_value ?? "",
        unit: productToEdit.unit ?? "",
        barcode: productToEdit.barcode || "",
        tags: productToEdit.tags || [],
        product_image: productToEdit.product_image || "",
        product_image_url: productToEdit.product_image_url
      });
      setTagInput("");
      setImageFile(null);
      setCategorySearch(getCategoryName(productToEdit.category_id));
      setBrandSearch(getBrandName(productToEdit.brand_id));
      setShowEditModal(true);
      setSearchParams({ modal: 'edit', id: String(productToEdit.product_id) });

      const shopId = await resolveShopIdForProduct(productToEdit.category_id, productToEdit.brand_id);
      setSelectedShopId(shopId);
      if (shopId) {
        fetchModalCategories(shopId);
        fetchModalBrands(shopId);
      } else {
        setModalCategories([]);
        setModalBrands([]);
      }
    }
  };

  const handleShopChange = (shopId: string) => {
    setSelectedShopId(shopId);
    setEditProduct(prev => ({ ...prev, category_id: undefined, brand_id: undefined }));
    if (shopId) {
      fetchModalCategories(shopId);
      fetchModalBrands(shopId);
    } else {
      setModalCategories([]);
      setModalBrands([]);
    }
  };

  const handleDelete = () => {
    if (selectedProducts.length === 0) {
      alert("Please select at least one product to delete");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      for (const productId of selectedProducts) {
        const response = await authenticatedFetch(`${API_BASE}/product/${productId}`, {
          method: "DELETE"
        }, cookies.auth);
        
        if (!response.ok) {
          console.error(`Failed to delete product ${productId}. Status:`, response.status);
        }
      }
      setSelectedProducts([]);
      setShowDeleteConfirm(false);
      await fetchProducts();
    } catch (error) {
      console.error("Error deleting products:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (product: Product) => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/product/${product.product_id}`, {
        method: "PUT",
        body: JSON.stringify({ is_active: !product.is_active })
      }, cookies.auth);

      if (response.ok) {
        snack.onResponse({
          message: `${product.product_name} marked ${!product.is_active ? "Active" : "Inactive"}`,
          status: 200
        });
        await fetchProducts();
      } else {
        snack.onResponse({ message: "Failed to update product status", status: response.status });
      }
    } catch (error) {
      console.error("Error toggling product status:", error);
      snack.onResponse({ message: "Network error updating product status", status: 500 });
    }
  };

  const handleSave = async (isEdit: boolean) => {
    try {
      setIsLoading(true);
      let imageBase64: string | null = null;
      
      if (imageFile) {
        try {
          imageBase64 = await convertFileToBase64(imageFile!);
        } catch (error) {
          console.error('Error converting image to base64:', error);
          return;
        }
      }

      // Validate required fields
      if (!editProduct.product_name.trim()) {
        snack.onResponse({ message: "Product name is required", status: 400 });
        return;
      }

      if (!editProduct.category_id || editProduct.category_id === undefined) {
        snack.onResponse({ message: "Please select a category", status: 400 });
        return;
      }

      if (!editProduct.brand_id || editProduct.brand_id === undefined) {
        snack.onResponse({ message: "Please select a brand", status: 400 });
        return;
      }

      const categoryId = parseInt(editProduct.category_id.toString());
      const brandId = parseInt(editProduct.brand_id.toString());

      if (isNaN(categoryId)) {
        snack.onResponse({ message: "Invalid category ID", status: 400 });
        return;
      }

      if (isNaN(brandId)) {
        snack.onResponse({ message: "Invalid brand ID", status: 400 });
        return;
      }

      const payload: Record<string, unknown> = {
        name: editProduct.product_name.trim(),
        description: editProduct.product_description.trim(),
        price: editProduct.price.toString(),
        // Always included, even empty — the backend treats an empty string
        // as "clear it to null". Omitting the key here when the field is
        // empty (as opposed to omitting the value) would leave whatever
        // purchase_price the product already had untouched on edit, since
        // the backend only updates fields whose key is present in the body.
        purchase_price: editProduct.purchase_price.toString(),
        quantity: editProduct.quantity.toString(),
        unit_value: editProduct.unit_value != null ? editProduct.unit_value.toString() : "",
        unit: editProduct.unit != null ? editProduct.unit.trim() : "",
        barcode: editProduct.barcode.trim(),
        tags: editProduct.tags,
        category_id: categoryId,
        brand_id: brandId,
        image: imageBase64 || editProduct.product_image
      };

      const url = isEdit 
        ? `${API_BASE}/product/${editProduct.product_id}`
        : `${API_BASE}/product`;
      
      const method = isEdit ? "PUT" : "POST";

      const response = await authenticatedFetch(url, {
        method,
        body: JSON.stringify(payload)
      }, cookies.auth);

      if (response.ok) {
        const responseData = await response.json();
        console.log("Success response:", responseData);

        setShowEditModal(false);
        setShowAddModal(false);
        setSearchParams({});
        setEditProduct({
          product_name: "",
          product_description: "",
          price: "",
          purchase_price: "",
          quantity: "",
          unit_value: "",
          unit: "",
          barcode: "",
          tags: [],
          product_image: "",
          category_id: undefined,
          brand_id: undefined
        });
        setTagInput("");
        setImageFile(null);
        resetDropdowns();
        setSelectedShopId("");
        setModalCategories([]);
        setModalBrands([]);
        setSelectedProducts([]);
        await fetchProducts();
      } else {
        snack.onResponse({ message: await parseApiError(response, "Failed to save product"), status: response.status });
      }
    } catch (error) {
      console.error("Error saving product:", error);
      snack.onResponse({ message: error instanceof Error ? error.message : "Failed to save product", status: 400 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedProducts(products.map(product => product.product_id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (productId: number, checked: boolean) => {
    if (checked) {
      setSelectedProducts([productId]);
    } else {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    }
  };

  const getCategoryName = (productOrId: Product | { category_id?: number | null; category_name?: string | null; Category?: { category_name?: string } | null } | number | null | undefined) => {
    if (typeof productOrId === 'object' && productOrId !== null) {
      if (productOrId.category_name) return productOrId.category_name;
      if (productOrId.Category?.category_name) return productOrId.Category.category_name;
      const categoryId = productOrId.category_id;
      if (!categoryId) return "Unknown";
      const category = categories.find(c => c.category_id === categoryId);
      return category ? category.category_name : "Unknown";
    }
    const categoryId = productOrId;
    if (!categoryId) return "Unknown";
    const category = categories.find(c => c.category_id === categoryId);
    return category ? category.category_name : "Unknown";
  };

  const getBrandName = (productOrId: Product | { brand_id?: number | null; brand_name?: string | null; Brand?: { brand_name?: string } | null } | number | null | undefined) => {
    if (typeof productOrId === 'object' && productOrId !== null) {
      if (productOrId.brand_name) return productOrId.brand_name;
      if (productOrId.Brand?.brand_name) return productOrId.Brand.brand_name;
      const brandId = productOrId.brand_id;
      if (!brandId) return "Unknown";
      const brand = brands.find(b => b.brand_id === brandId);
      return brand ? brand.brand_name : "Unknown";
    }
    const brandId = productOrId;
    if (!brandId) return "Unknown";
    const brand = brands.find(b => b.brand_id === brandId);
    return brand ? brand.brand_name : "Unknown";
  };

  const resetDropdowns = () => {
    setCategorySearch("");
    setBrandSearch("");
  };

  const performBarcodeLookup = async (rawBarcode: string) => {
    const barcode = rawBarcode.trim();
    if (!barcode) return;
    setIsScanLoading(true);
    try {
      const response = await authenticatedFetch(
        `${API_BASE}/product/barcode/${encodeURIComponent(barcode)}`,
        { method: 'GET' },
        cookies.auth
      );
      if (response.ok) {
        const found: Product = await response.json();
        setSelectedProducts([found.product_id]);
        setHighlightedProductId(found.product_id);
        if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = setTimeout(() => setHighlightedProductId(null), 3000);
        document.getElementById(`product-row-${found.product_id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        snack.onResponse({ message: `Found: ${found.product_name}`, status: 200 });
      } else if (response.status === 404) {
        snack.onResponse({ message: `No product found for barcode: ${barcode}`, status: 404 });
      } else {
        snack.onResponse({ message: "Error looking up barcode", status: response.status });
      }
    } catch {
      snack.onResponse({ message: "Network error during barcode lookup", status: 500 });
    } finally {
      setIsScanLoading(false);
      setScanInput("");
      scanInputRef.current?.focus();
    }
  };

  const handleScanKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    await performBarcodeLookup(scanInput);
  };

  const handlePrintBarcode = () => {
    const product = products.find(p => p.product_id === selectedProducts[0]);
    if (product) {
      setPrintProduct(product);
      setPrintBarcodeInput("");
      setShowPrintModal(true);
    }
  };

  const generateBarcode = (productId: number): string =>
    `PRD${String(productId).padStart(8, '0')}`;

  const assignBarcode = async (barcode: string) => {
    if (!printProduct || !barcode.trim()) return;
    setIsAssigningBarcode(true);
    try {
      const response = await authenticatedFetch(
        `${API_BASE}/product/${printProduct.product_id}`,
        { method: 'PUT', body: JSON.stringify({ barcode: barcode.trim() }) },
        cookies.auth
      );
      if (response.ok) {
        setPrintProduct(prev => prev ? { ...prev, barcode: barcode.trim() } : prev);
        await fetchProducts();
        snack.onResponse({ message: "Barcode assigned successfully", status: 200 });
      } else {
        snack.onResponse({ message: "Failed to assign barcode", status: response.status });
      }
    } catch {
      snack.onResponse({ message: "Network error assigning barcode", status: 500 });
    } finally {
      setIsAssigningBarcode(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  // Gated on isPrintingBarcodeLabel rather than looked up unconditionally —
  // #print-root is static markup that can't change, so there's no reason
  // to pay for this DOM lookup on every ProductPage render (search typing,
  // pagination, row selection, ...) when the print modal isn't even open.
  // Looked up fresh (not cached in state/ref) on the renders where it does
  // run, so the portal never holds a stale reference if this element were
  // somehow re-created — see the null-guard where this is used below.
  const printPortalRoot = isPrintingBarcodeLabel ? document.getElementById('print-root') : null;

  return (
    <div className={style.wrapper}>
      {/* Header Section */}
      <div className={style.header}>
        <h1 className={style.title}>Product</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          <div className={style.actionButtons}>
            {permissions.canAdd && (
              <>
                <button className={style.actionBtn} onClick={handleAdd}>
                  Add
                </button>
                <button className={style.actionBtn} onClick={handleUpdate}>
                  Update
                </button>
                <button className={style.actionBtnDanger} onClick={handleDelete}>
                  Delete
                </button>
              </>
            )}
            <button
              className={style.actionBtn}
              onClick={handlePrintBarcode}
              disabled={selectedProducts.length !== 1}
            >
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Barcode Scanner Bar */}
      <div className={style.scanSection}>
        <span className={style.scanLabel}>Barcode Scanner</span>
        <input
          ref={scanInputRef}
          type="text"
          placeholder="Scan or type barcode..."
          value={scanInput}
          onChange={(e) => setScanInput(e.target.value)}
          onKeyDown={handleScanKeyDown}
          className={style.scanInput}
          disabled={isScanLoading}
          autoComplete="off"
        />
        {isScanLoading && <span className={style.scanSpinner}>Searching...</span>}
        <button
          type="button"
          className={style.cameraScanButton}
          onClick={() => setShowScannerModal("search")}
          title="Scan with camera"
        >
          <FontAwesomeIcon icon={faCamera} />
        </button>
      </div>

      {/* Table Section */}
      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {permissions.canEdit && (
                <th>
                  <input
                    type="checkbox"
                    checked={selectedProducts.length === products.length && products.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={style.checkbox}
                  />
                </th>
              )}
              <th>Product ID</th>
              <th>Name</th>
              <th>Description</th>
              <th>Price</th>
              <th>Quantity</th>
              <th>Unit / Size</th>
              <th>Barcode</th>
              <th>Tags</th>
              <th>Status</th>
              <th>Category</th>
              <th>Brand</th>
              <th>Image</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr
                key={product.product_id}
                id={`product-row-${product.product_id}`}
                className={`${style.tableRow} ${highlightedProductId === product.product_id ? style.highlightedRow : ''}`}
              >
                {permissions.canEdit && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.product_id)}
                      onChange={(e) => handleSelectProduct(product.product_id, e.target.checked)}
                      className={style.checkbox}
                    />
                  </td>
                )}
                <td>{product.product_id}</td>
                <td>{product.product_name || 'N/A'}</td>
                <td className={style.descriptionCell}>
                  <TruncatedText text={product.product_description || 'N/A'} maxLength={30} />
                </td>
                <td>{product.price ? product.price.toFixed(2) : '0.00'}</td>
                <td>{product.quantity || 0}</td>
                <td>{product.weight || (product.unit_value && product.unit ? `${product.unit_value} ${product.unit}` : '-')}</td>
                <td>{product.barcode || 'N/A'}</td>
                <td>{product.tags && product.tags.length > 0 ? product.tags.join(', ') : 'N/A'}</td>
                <td>
                  <span
                    className={`${style.statusBadge} ${product.is_active ? style.statusActive : style.statusInactive} ${permissions.canEdit ? style.statusBadgeClickable : ''}`}
                    onClick={permissions.canEdit ? () => handleToggleActive(product) : undefined}
                    title={permissions.canEdit ? "Click to toggle" : undefined}
                  >
                    {product.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{getCategoryName(product)}</td>
                <td>{getBrandName(product)}</td>
                <td>
                  <img
                    src={product.product_image_url ?
                      resolveImageUrl(product.product_image_url) :
                      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzMEMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik0zMCA0MEMzMi43NjE0IDQwIDM1IDM3Ljc2MTQgMzUgMzVDMzUgMzIuMjM4NiAzMi43NjE0IDMwIDMwIDMwQzI3LjIzODYgMzAgMjUgMzIuMjM4NiAyNSAzNUMyNSAzNy43MjE0IDI3LjIzODYgNDAgMzAgNDBaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                    }
                    alt={product.product_name}
                    className={style.productImage}
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzMEMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik0zMCA0MEMzMi43NjE0IDQwIDM1IDM3Ljc2MTQgMzUgMzVDMzUgMzIuMjM4NiAzMi43NjE0IDMwIDMwIDMwQzI3LjIzODYgMzAgMjUgMzIuMjM4NiAyNSAzNUMyNSAzNy43MjE0IDI3LjIzODYgNDAgMzAgNDBaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                    }}
                  />
                </td>
                <td>{formatDate(product.created_at)}</td>
                <td>{formatDate(product.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>{showAddModal ? "Add New Product" : "Update Product"}</h2>
            <div className={style.modalContent}>
              <input
                type="text"
                placeholder="Product Name *"
                value={editProduct.product_name}
                onChange={(e) => setEditProduct({...editProduct, product_name: e.target.value})}
                className={style.modalInput}
                required
              />
              <select
                value={selectedShopId}
                onChange={(e) => handleShopChange(e.target.value)}
                className={style.modalInput}
                required
              >
                <option value="">Select Shop *</option>
                {shops.map(shop => (
                  <option key={shop.shop_id} value={shop.shop_id}>
                    {shop.shop_name}
                  </option>
                ))}
              </select>
              <select
                value={editProduct.category_id || ""}
                onChange={(e) => {
                  const categoryId = parseInt(e.target.value);
                  const category = modalCategories.find(c => c.category_id === categoryId);
                  if (category) {
                    setEditProduct({...editProduct, category_id: categoryId});
                  }
                }}
                className={style.modalInput}
                disabled={!selectedShopId}
                required
              >
                <option value="">{selectedShopId ? "Select Category *" : "Select a shop first"}</option>
                {modalCategories.map(category => (
                  <option key={category.category_id} value={category.category_id}>
                    {category.category_name}
                  </option>
                ))}
              </select>
              <select
                value={editProduct.brand_id || ""}
                onChange={(e) => {
                  const brandId = parseInt(e.target.value);
                  const brand = modalBrands.find(b => b.brand_id === brandId);
                  if (brand) {
                    setEditProduct({...editProduct, brand_id: brandId});
                  }
                }}
                className={style.modalInput}
                disabled={!selectedShopId}
                required
              >
                <option value="">{selectedShopId ? "Select Brand *" : "Select a shop first"}</option>
                {modalBrands.map(brand => (
                  <option key={brand.brand_id} value={brand.brand_id}>
                    {brand.brand_name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Enter price *"
                value={editProduct.price}
                onChange={(e) => setEditProduct({...editProduct, price: e.target.value})}
                className={style.modalInput}
                required
              />
              <input
                type="text"
                placeholder="Enter purchase price (optional)"
                value={editProduct.purchase_price}
                onChange={(e) => setEditProduct({...editProduct, purchase_price: e.target.value})}
                className={style.modalInput}
              />
              <input
                type="text"
                placeholder="Enter quantity *"
                value={editProduct.quantity}
                onChange={(e) => setEditProduct({...editProduct, quantity: e.target.value})}
                className={style.modalInput}
                required
              />
              <div className={style.measurementRow}>
                <input
                  type="text"
                  placeholder="Size / Measurement (e.g. 500, 1) (optional)"
                  value={editProduct.unit_value ?? ""}
                  onChange={(e) => setEditProduct({...editProduct, unit_value: e.target.value})}
                  className={style.modalInput}
                />
                <input
                  type="text"
                  placeholder="Unit (e.g. piece, item, dozen, half dozen, gm, kg) (optional)"
                  list="measurement-units-list"
                  value={editProduct.unit ?? ""}
                  onChange={(e) => setEditProduct({...editProduct, unit: e.target.value})}
                  className={style.modalInput}
                />
                <datalist id="measurement-units-list">
                  <option value="item" />
                  <option value="piece" />
                  <option value="pack" />
                  <option value="packet" />
                  <option value="box" />
                  <option value="bottle" />
                  <option value="can" />
                  <option value="dozen" />
                  <option value="half dozen" />
                  <option value="gm" />
                  <option value="kg" />
                  <option value="ml" />
                  <option value="L" />
                  <option value="sachet" />
                  <option value="roll" />
                  <option value="bundle" />
                </datalist>
              </div>
              <div className={style.barcodeInputRow}>
                <input
                  type="text"
                  placeholder="Enter product barcode"
                  value={editProduct.barcode}
                  onChange={(e) => setEditProduct({...editProduct, barcode: e.target.value})}
                  className={style.modalInput}
                  required
                />
                <button
                  type="button"
                  className={style.cameraScanButton}
                  onClick={() => setShowScannerModal("add")}
                  title="Scan with camera"
                >
                  <FontAwesomeIcon icon={faCamera} />
                </button>
              </div>
              <div className={style.tagInputWrapper}>
                <input
                  type="text"
                  placeholder="Type a tag and press Enter or comma (e.g. organic, bestseller)"
                  value={tagInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.endsWith(',')) {
                      commitTag(value.slice(0, -1));
                      setTagInput('');
                    } else {
                      setTagInput(value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitTag(tagInput);
                      setTagInput('');
                    } else if (e.key === 'Backspace' && tagInput === '' && editProduct.tags.length > 0) {
                      setEditProduct({...editProduct, tags: editProduct.tags.slice(0, -1)});
                    }
                  }}
                  className={style.modalInput}
                />
                {editProduct.tags.length > 0 && (
                  <div className={style.tagChipRow}>
                    {editProduct.tags.map((tag, idx) => (
                      <span key={idx} className={style.tagChip}>
                        {tag}
                        <button
                          type="button"
                          className={style.tagChipRemove}
                          onClick={() => setEditProduct({...editProduct, tags: editProduct.tags.filter((_, i) => i !== idx)})}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <textarea
                placeholder="Product Description"
                value={editProduct.product_description}
                onChange={(e) => setEditProduct({...editProduct, product_description: e.target.value})}
                className={style.modalInput}
              />
              <input
                type="file"
                onChange={handleImageChange}
                className={style.modalInput}
                accept="image/*"
              />
              {editProduct.product_image && !imageFile && (
                <img
                  src={resolveImageUrl(editProduct.product_image_url)}
                  alt="Current product"
                  className={style.currentImage}
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzNUMyNSAzMy43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                  }}
                />
              )}
              <div className={style.modalButtons}>
                <button
                  onClick={() => handleSave(showEditModal)}
                  className={style.saveBtn}
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : (showAddModal ? "Add Product" : "Update Product")}
                </button>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSearchParams({});
                    setSelectedShopId("");
                    setModalCategories([]);
                    setModalBrands([]);
                  }}
                  className={style.cancelBtn}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showScannerModal && (
        <BarcodeScannerModal
          onScan={(value) => {
            if (showScannerModal === "add") {
              setEditProduct(prev => ({...prev, barcode: value}));
              setShowScannerModal(null);
            } else {
              setShowScannerModal(null);
              performBarcodeLookup(value);
            }
          }}
          onClose={() => {
            const wasSearch = showScannerModal === "search";
            setShowScannerModal(null);
            if (wasSearch) scanInputRef.current?.focus();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete {selectedProducts.length} selected product(s)?</p>
            <div className={style.modalButtons}>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>
                {isLoading ? "Deleting..." : "Yes, Delete"}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className={style.cancelBtn}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Barcode Modal */}
      {showPrintModal && printProduct && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Print Barcode Label</h2>

            {!printProduct.barcode ? (
              <div className={style.noBarcodeSection}>
                <p className={style.noBarcodeText}>
                  This product has no barcode. Assign one before printing.
                </p>
                <div className={style.assignBarcodeSection}>
                  <p className={style.assignLabel}>Scan or type a barcode to assign:</p>
                  <div className={style.assignRow}>
                    <input
                      type="text"
                      placeholder="Scan or type barcode..."
                      value={printBarcodeInput}
                      onChange={(e) => setPrintBarcodeInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') assignBarcode(printBarcodeInput); }}
                      className={style.assignBarcodeInput}
                      autoFocus
                      autoComplete="off"
                    />
                    <button
                      onClick={() => assignBarcode(printBarcodeInput)}
                      className={style.assignBtn}
                      disabled={!printBarcodeInput.trim() || isAssigningBarcode}
                    >
                      {isAssigningBarcode ? "Assigning..." : "Assign"}
                    </button>
                  </div>
                  <div className={style.orDivider}><span>OR</span></div>
                  <button
                    onClick={() => assignBarcode(generateBarcode(printProduct.product_id))}
                    className={style.generateBtn}
                    disabled={isAssigningBarcode}
                  >
                    {isAssigningBarcode ? "Generating..." : "Auto-Generate Barcode"}
                  </button>
                  <p className={style.generateHint}>
                    Will assign: PRD{String(printProduct.product_id).padStart(8, '0')}
                  </p>
                </div>
              </div>
            ) : (
              <div className={style.printModalContent}>
                <div className={style.printLabel}>
                  <BarcodeLabelContent product={printProduct} />
                </div>
              </div>
            )}

            {/* Print-only copy, rendered into #print-root (a sibling of
                #root — see public/index.html and index.css's @media print
                rules). Keeping it out of the main app tree means printing
                doesn't depend on hiding the rest of ProductPage in place —
                a long product list underneath would otherwise still occupy
                layout and drive print pagination even while hidden.
                #print-root's existence depends on public/index.html's
                markup, not anything this component controls (a test
                mounting ProductPage in isolation, or a future markup
                change, could remove it) — createPortal throws on a null
                container, so this only renders once one's actually found,
                rather than crashing the whole modal. */}
            {printProduct.barcode && printPortalRoot && createPortal(
              <div className={style.printLabel} id="barcode-print-label">
                <BarcodeLabelContent product={printProduct} />
              </div>,
              printPortalRoot
            )}

            <div className={style.modalButtons}>
              {printProduct.barcode && (
                <button
                  onClick={() => {
                    // Small delay lets Firefox finish rendering the barcode SVG before opening print dialog
                    setTimeout(() => window.print(), 150);
                  }}
                  className={style.saveBtn}
                >
                  Print
                </button>
              )}
              <button
                onClick={() => { setShowPrintModal(false); setPrintProduct(null); setPrintBarcodeInput(""); }}
                className={style.cancelBtn}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage;